import { Decimal } from "decimal.js";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { DRAFT_PAYMENT_FIELDS } from "./get-draft-payments";

import { useTenant } from "@/providers/session";
import { type DocumentNode, gql, useApolloClient } from "@apollo/client";
import { useMutation } from "@apollo/client";

type UpdateDraftPaymentInput = {
  draftPaymentId: string;
  rawAmount: number | null;
  amount: number | null;
  executionDate: Date | null;
  recipientName: string | null;
  recipientIban: string | null;
  purpose: string | null;
  currency: string | null;
  discount: number | null;
};

export const useUpdateDraftPayment = ({
  refetchQueries,
}: {
  refetchQueries?: string[] | DocumentNode[];
}) => {
  const { t } = useTranslation();
  const { tenantId } = useTenant();
  const client = useApolloClient();

  const [updatePayment, { loading, error, data }] = useMutation(
    UPDATE_PAYMENT_MUTATION,
    {
      refetchQueries,
      optimisticResponse: (vars) => {
        const cache = client.cache;
        const existingPayment = cache.readFragment({
          id: `DraftPayment:${vars.draftPaymentId}`,
          fragment: DRAFT_PAYMENT_FIELDS,
          fragmentName: "DraftPaymentFields",
        });

        if (!existingPayment) {
          return {
            updatePayment: null,
          };
        }

        return {
          updatePayment: {
            __typename: "DraftPayment",
            ...existingPayment,
            ...vars,
            ...(vars.amount != null && {
              amount: new Decimal(vars.amount).mul(100).toNumber(),
            }),
          },
        };
      },
      update: (cache, { data: responseData }) => {
        if (!responseData?.updatePayment) return;

        cache.writeFragment({
          id: `DraftPayment:${responseData.updatePayment.draftPaymentId}`,
          fragment: DRAFT_PAYMENT_FIELDS,
          fragmentName: "DraftPaymentFields",
          data: responseData.updatePayment,
        });
      },
    },
  );

  const handleUpdateDraftPayment = async (
    input: Partial<UpdateDraftPaymentInput>,
  ) => {
    if (!tenantId) {
      throw new Error("Tenant ID is required");
    }

    const toastId = toast.loading(t("generic_loading"));
    console.log("input", input);
    try {
      const { data: response } = await updatePayment({
        variables: {
          tenantId,
          ...input,
          ...(input.amount != null && {
            amount: new Decimal(input.amount).mul(100).toNumber(),
          }),
        },
      });

      toast.dismiss(toastId);

      return response.updateDraftPayment;
    } catch (error) {
      toast.error(t("generic_error"), { id: toastId, richColors: true });
      throw error;
    }
  };

  return {
    updateDraftPayment: handleUpdateDraftPayment,
    loading,
    error,
    data: data?.updatePayment,
  };
};

export const UPDATE_PAYMENT_MUTATION = gql`
  mutation UpdateDraftPayment(
    $tenantId: String!
    $draftPaymentId: String!
    $rawAmount: Float
    $executionDate: String
    $recipientName: String
    $recipientIban: String
    $purpose: String
    $currency: String
    $discount: Float
  ) {
    updateDraftPayment(
      tenantId: $tenantId
      draftPaymentId: $draftPaymentId
      rawAmount: $rawAmount
      executionDate: $executionDate
      recipientName: $recipientName
      recipientIban: $recipientIban
      purpose: $purpose
      currency: $currency
      discount: $discount
    ) {
      ...DraftPaymentFields
    }
  }
  ${DRAFT_PAYMENT_FIELDS}
`;
